import { useComputed } from '@preact/signals';
import type { JSX } from 'preact/jsx-runtime';
import type { TargetedEvent } from 'preact';
import cn from 'classnames';
import { useState } from 'preact/hooks';
import { Button } from '@nespresso-design-system/components';
import type { Product } from '@/domain/Product';
import styles from './FavoriteButton.module.scss';
import useFavorites from '@/widgets/hooks/useFavorites';
import useTranslations from '@/widgets/hooks/useTranslations';
import type { Translations } from '@/widgets/ProductList/config';
import useRoute from '@/widgets/hooks/useRoute';
import ModalOnDemand from '@/widgets/components/controls/ModalOnDemand/ModalOnDemand';

type FavoriteButtonProps = {
  product: Product;
  itemListName: string;
  class?: string;
};

export default function FavoriteButton({
  product,
  itemListName,
  class: className
}: FavoriteButtonProps) {
  const { inFavorites } = useRoute();
  const { t } = useTranslations<Translations>();
  const {
    isFavoriteFeatureEnabled,
    isFavorite,
    toggleFavorite,
    optimisticRemovalOfFav
  } = useFavorites(itemListName, product);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const actionText = useComputed(() =>
    isFavorite.value
      ? t('common.product.removeFavorite')
      : t('common.product.addFavorite')
  );

  const buttonClassName = useComputed(() =>
    cn(styles.heart, {
      [styles.liked]: isFavorite.value
    })
  );

  async function onSubmit(e: TargetedEvent<HTMLFormElement>) {
    e.preventDefault();
    e.stopPropagation();

    const inAlternativesFavorites =
      typeof window !== 'undefined' &&
      inFavorites &&
      window?.location?.search?.indexOf(
        'favorites_openModal=view-alternatives'
      ) !== -1;

    if (inFavorites) {
      if (inAlternativesFavorites) {
        toggleFavorite();
      } else {
        setIsOpen(true);
      }
    } else {
      toggleFavorite();
    }
  }

  if (!isFavoriteFeatureEnabled.value) return null;

  return (
    <>
      <ModalOnDemand
        size="small"
        heading={t('common.product.removeFavorite')}
        position="center"
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
        closeBtnA11yLabel={t('components.modal.closeBtnA11yLabel')}
        footerSlot={
          <>
            <Button
              disabled={!isOpen}
              label={t('favorites.cancel')}
              variant="outline"
              onClick={(e: JSX.TargetedMouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                e.stopPropagation();

                setIsOpen(false);
              }}
            />
            <Button
              disabled={!isOpen}
              label={t('favorites.remove')}
              variant="secondary"
              onClick={async (e: JSX.TargetedMouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                e.stopPropagation();

                setIsOpen(false);
                const wasToggled = await toggleFavorite();

                if (wasToggled) optimisticRemovalOfFav(product.id);
              }}
            />
          </>
        }
      >
        {t('favorites.areYouSureRemoval')}
      </ModalOnDemand>

      <form onSubmit={onSubmit} class={className ?? styles.favoriteButton}>
        <button
          class={buttonClassName}
          title={actionText}
          aria-label={actionText}
          type="submit"
          onClick={(e) => e.stopPropagation()}
        />
      </form>
    </>
  );
}
